/**
 * Who has approved the open merge request, and the one button that follows.
 *
 * Issue #99, decision 4: GITLAB ALONE DECIDES. The button is drawn from
 * `user_can_approve` and `user_has_approved` as GitLab reported them and from
 * nothing else - no role, no reviewer list, and no rule about authors approving
 * their own work. Where GitLab says nothing (null) neither button is drawn, and
 * where it says no the person is simply not offered one; the guard against the
 * wrong person approving is how the team works, not code here.
 *
 * Approve sends the head the page loaded (`head_sha`), so an approval is of the
 * commit the person looked at. If the branch has moved GitLab refuses (409), and
 * the card is re-read so the next click is on the new head.
 *
 * A refusal is a toast in GitLab's own words and never says "sign in": the
 * server answers a person GitLab will not let approve with 403, and this client
 * reads only a 401 as an expired session.
 */

import { useState } from 'react'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { api } from '../../api'
import type { ApiError } from '../../api'
import type { MergeRequest } from '../../schema'
import { useToasts } from '../../state/Toasts'

/** "1 of 2", or "1" when nothing is required of anybody. */
function tally(approved: number, required: number): string {
  return required > 0 ? `${approved} of ${required}` : String(approved)
}

export default function ApprovalRow({
  slug,
  proposal,
  onChanged,
}: {
  slug: string
  proposal: MergeRequest
  /** Re-read the merge request, so the card shows what GitLab now holds. */
  onChanged: () => void
}) {
  const toasts = useToasts()
  const [busy, setBusy] = useState(false)
  const approvals = proposal.approvals ?? null

  // Nothing known: no row at all, as the code-owners line is absent when none
  // are known. A GitLab tier without approvals reads this way.
  if (!approvals) return null

  const approved = approvals.approved_by
  const canApprove = approvals.user_can_approve === true
  const hasApproved = approvals.user_has_approved === true
  // Say something only when there is something to say or to do.
  if (approvals.required === 0 && approved.length === 0 && !canApprove && !hasApproved) {
    return null
  }

  const act = async () => {
    setBusy(true)
    try {
      if (hasApproved) {
        await api.unapproveMerge(slug)
        toasts.succeeded(`Your approval of !${proposal.iid} is revoked`)
      } else {
        // The head this page loaded, not whatever the branch is now.
        await api.approveMerge(slug, { sha: proposal.head_sha || undefined })
        toasts.succeeded(`You approved !${proposal.iid}`)
      }
      onChanged()
    } catch (failure) {
      const refused = failure as ApiError
      toasts.failed(refused.message || 'GitLab refused')
      // A 409 means the head moved; a 403 may mean what GitLab allows changed.
      // Either way the card is out of date.
      if (refused.status === 409 || refused.status === 403) onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
      data-testid="approval-row"
    >
      <Typography variant="caption" color="text.secondary">
        Approvals:
      </Typography>
      <Typography variant="caption">
        {approved.length === 0 && approvals.required === 0
          ? 'none yet'
          : tally(approved.length, approvals.required)}
      </Typography>
      {approved.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          {approved.map((person) => person.name || person.username).join(', ')}
        </Typography>
      ) : null}
      {canApprove || hasApproved ? (
        <Button
          size="small"
          variant={hasApproved ? 'outlined' : 'contained'}
          onClick={() => void act()}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
          sx={{ minWidth: 0, py: 0 }}
        >
          {hasApproved ? 'Revoke approval' : 'Approve'}
        </Button>
      ) : null}
    </Stack>
  )
}
